import React, { useState, useEffect, useRef, useCallback } from 'react';
import { BrokerSearchResult } from '@/types/referral.types';
import { referralsService } from '@/services/referrals.service';

interface BrokerSearchSelectProps {
  value: BrokerSearchResult | null;
  onChange: (broker: BrokerSearchResult | null) => void;
  placeholder?: string;
  label?: string;
  error?: string;
  disabled?: boolean;
  className?: string;
}

export const BrokerSearchSelect: React.FC<BrokerSearchSelectProps> = ({
  value,
  onChange,
  placeholder = 'Search brokers by name, firm, or phone...',
  label,
  error,
  disabled = false,
  className = '',
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [results, setResults] = useState<BrokerSearchResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);

  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Debounced search function
  const searchBrokers = useCallback(async (query: string) => {
    if (query.length < 2) {
      setResults([]);
      return;
    }

    try {
      setIsLoading(true);
      setSearchError(null);
      const response = await referralsService.searchBrokers(query);
      setResults(response.data);
    } catch (err) {
      setSearchError('Failed to search brokers');
      setResults([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Handle search input change with debounce
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setSearchQuery(query);
    setIsOpen(true);

    // Clear previous debounce
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    // Debounce the search
    debounceRef.current = setTimeout(() => {
      searchBrokers(query);
    }, 300);
  };

  // Handle broker selection
  const handleSelect = (broker: BrokerSearchResult) => {
    onChange(broker);
    setSearchQuery('');
    setResults([]);
    setIsOpen(false);
  };

  // Handle clear selection
  const handleClear = () => {
    onChange(null);
    setSearchQuery('');
    setResults([]);
    inputRef.current?.focus();
  };

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  return (
    <div className={`w-full ${className}`} ref={wrapperRef}>
      {label && (
        <label className="block text-sm font-medium text-secondary-700 mb-1.5">
          {label}
        </label>
      )}

      <div className="relative">
        {/* Selected broker display */}
        {value ? (
          <div className="flex items-center justify-between px-4 py-2.5 border border-secondary-300 rounded-lg bg-white">
            <div className="flex-1">
              <p className="text-sm font-medium text-secondary-900">{value.full_name}</p>
              <p className="text-xs text-secondary-500">
                {value.firm_name && `${value.firm_name} - `}
                {value.city && `${value.city} - `}
                {value.phone}
              </p>
            </div>
            {!disabled && (
              <button
                type="button"
                onClick={handleClear}
                className="ml-2 p-1 text-secondary-400 hover:text-secondary-600 hover:bg-secondary-100 rounded-full transition-colors"
                aria-label="Clear selection"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
        ) : (
          /* Search input */
          <>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-secondary-400">
                {isLoading ? (
                  <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                )}
              </div>
              <input
                ref={inputRef}
                type="text"
                value={searchQuery}
                onChange={handleSearchChange}
                onFocus={() => setIsOpen(true)}
                placeholder={placeholder}
                disabled={disabled}
                className={`
                  block w-full rounded-lg border bg-white pl-10 pr-4 py-2.5 text-secondary-900
                  placeholder-secondary-400 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-0
                  ${error ? 'border-danger-500 focus:border-danger-500 focus:ring-danger-500' : 'border-secondary-300 focus:border-primary-500 focus:ring-primary-500'}
                  ${disabled ? 'bg-secondary-50 cursor-not-allowed' : ''}
                `}
              />
            </div>

            {/* Dropdown results */}
            {isOpen && searchQuery.length >= 2 && (
              <div className="absolute z-20 w-full mt-1 bg-white border border-secondary-200 rounded-lg shadow-lg max-h-60 overflow-auto">
                {searchError ? (
                  <div className="px-4 py-3 text-sm text-danger-600">{searchError}</div>
                ) : isLoading ? (
                  <div className="px-4 py-3 text-sm text-secondary-500">Searching...</div>
                ) : results.length === 0 ? (
                  <div className="px-4 py-3 text-sm text-secondary-500">
                    No brokers found matching "{searchQuery}"
                  </div>
                ) : (
                  <ul className="py-1">
                    {results.map((broker) => (
                      <li key={broker.id}>
                        <button
                          type="button"
                          onClick={() => handleSelect(broker)}
                          className="w-full text-left px-4 py-3 hover:bg-secondary-50 transition-colors"
                        >
                          <p className="text-sm font-medium text-secondary-900">{broker.full_name}</p>
                          <p className="text-xs text-secondary-500">
                            {broker.firm_name && `${broker.firm_name}`}
                            {broker.city && ` - ${broker.city}`}
                            {broker.phone && ` - ${broker.phone}`}
                          </p>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {error && (
        <p className="mt-1.5 text-sm text-danger-600" role="alert">
          {error}
        </p>
      )}
    </div>
  );
};

export default BrokerSearchSelect;
